import type { ReactNode } from 'react';
import { CaretRight, House } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type Crumb = { label: ReactNode; href?: string };

export type BreadcrumbsProps = {
  /** From the root to the current page; the last one is the current page and is not a link. */
  items: Crumb[];
  /** Accessible name of the trail, localised by the caller ("Breadcrumb"). */
  label: string;
  /** Light text for dark heroes. */
  onDark?: boolean;
  /** Prepend a Home crumb (house icon; the label is for screen readers). */
  home?: { href: string; label: string };
  className?: string;
};

/** Where this page sits. The current page truncates first so the trail stays on one line. */
export function Breadcrumbs({ items: trail, label, onDark = false, home, className }: BreadcrumbsProps) {
  const items: Crumb[] = home
    ? [{ label: <Icon icon={House} size="sm" label={home.label} className="shrink-0" />, href: home.href }, ...trail]
    : trail;
  const last = items.length - 1;
  return (
    <nav aria-label={label} className={cx('min-w-0', className)}>
      <ol
        className={cx(
          'flex min-w-0 items-center gap-1 text-sm',
          onDark ? 'text-jc-readout-muted' : 'text-jc-ink-secondary',
        )}
      >
        {items.map((item, index) => (
          <li key={index} className={cx('flex items-center gap-1', index === last ? 'min-w-0' : 'shrink-0')}>
            {index === last || !item.href ? (
              <span
                aria-current={index === last ? 'page' : undefined}
                className={cx(
                  'truncate',
                  index === last && 'font-semibold',
                  index === last && (onDark ? 'text-jc-readout-ink' : 'text-jc-ink'),
                )}
              >
                {item.label}
              </span>
            ) : (
              <a
                href={item.href}
                className={cx(
                  'rounded-jc-info underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
                  onDark ? 'hover:text-jc-readout-ink' : 'hover:text-jc-ink',
                )}
              >
                {item.label}
              </a>
            )}
            {index < last ? <Icon icon={CaretRight} size="sm" className="shrink-0 opacity-60" /> : null}
          </li>
        ))}
      </ol>
    </nav>
  );
}
